import React, {useMemo, useState} from 'react';
import axios from 'axios';
import '../styles/SubscriptionTable.css';

const SubscriptionTable = ({ products, subscriptions }) => {
    const [initsubscriptions, setSubscriptions] = useState(subscriptions);
    const [itemList, setItemList] = useState(products);
    const [sortKey, setSortKey] = useState(''); // 정렬 기준
    const [sortOrder, setSortOrder] = useState('asc'); // 정렬 순서 ('asc', 'desc')
    const [filterStatus, setFilterStatus] = useState('all'); // 사용 여부 필터 ('all', 'active', 'inactive')
    const [alertVisible, setAlertVisible] = useState(false); // Alert 창 표시 상태
    const [selectedMacAddress, setSelectedMacAddress] = useState(null); // 삭제 대상 MAC Address
    const [selectedItemId, setSelectedItemId] = useState(null); // 삭제 대상 Item ID
    const [selectedHashKey, setSelectedHashKey] = useState(null); // 삭제 대상 Hash Key

    console.log('상품 리스트:', products);

    // 필터링 및 정렬 로직
    const getFilteredAndSortedSubscriptions = () => {
        const filteredSubscriptions = initsubscriptions.filter((sub) => {
            if (filterStatus === 'all') return true;
            return filterStatus === 'active' ? sub.useState === 'active' : sub.useState === 'inactive';
        });

        return filteredSubscriptions.sort((a, b) => {
            let valueA, valueB;

            switch (sortKey) {
                case 'name':
                    valueA = a.buyerName.toLowerCase();
                    valueB = b.buyerName.toLowerCase();
                    break;
                case 'creationDate':
                    valueA = new Date(a.creationDate);
                    valueB = new Date(b.creationDate);
                    break;
                case 'expirationDate':
                    valueA = new Date(a.expirationDate);
                    valueB = new Date(b.expirationDate);
                    break;
                case 'productNumber':
                    valueA = a.productNumber;
                    valueB = b.productNumber;
                    break;
                case 'useState':
                    valueA = a.useState.toLowerCase();
                    valueB = b.useState.toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
            } else {
                return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
            }
        });
    };

    const filteredAndSortedSubscriptions = getFilteredAndSortedSubscriptions();

    const handleDeleteClick = (itemId, hashKey, macAddress) => {
        setSelectedItemId(itemId);
        setSelectedHashKey(hashKey);
        setSelectedMacAddress(macAddress);
        setAlertVisible(true); // Alert 창 표시
    };

    const handleConfirmDelete = async () => {
        if (selectedItemId && selectedHashKey && selectedMacAddress) {
            try {
                const response = await axios.delete('/api/subscription/hash-key-mac-delete/temp', {
                    params: {
                        id: selectedItemId,
                        hashKey: selectedHashKey,
                        macAddress: selectedMacAddress,
                    },
                });

                if (response.data.result === 'success') {
                    alert('MAC 주소 삭제 성공');

                    // 삭제된 데이터를 UI에서 제거
                    const updatedSubscriptions = subscriptions.map((sub) => {
                        if (sub.productNumber === selectedItemId) {
                            // 해당 구독 항목의 macAddress를 제거
                            return {
                                ...sub,
                                macAddress1: sub.macAddress1 === selectedMacAddress ? null : sub.macAddress1,
                                macAddress2: sub.macAddress2 === selectedMacAddress ? null : sub.macAddress2,
                            };
                        }
                        return sub;
                    });

                    // 새로운 구독 데이터로 상태 업데이트
                    setSubscriptions(updatedSubscriptions);
                } else {
                    alert(`삭제 실패: ${response.data.message}`);
                }
            } catch (error) {
                console.error('삭제 요청 실패:', error);
                alert('MAC 주소 삭제 요청에 실패했습니다.');
            }
        }

        setAlertVisible(false);
        setSelectedItemId(null);
        setSelectedHashKey(null);
        setSelectedMacAddress(null);
    };

    const handleCancelDelete = () => {
        setAlertVisible(false);
        setSelectedItemId(null);
        setSelectedHashKey(null);
        setSelectedMacAddress(null);
    };

    const handleCopyHashKey = (e) => {
        const hashKey = e.target.innerText;
        navigator.clipboard.writeText(hashKey)
            .then(() => {
                alert('Hash Key가 클립보드에 복사되었습니다.');
            })
            .catch((err) => {
                console.error('Hash Key 복사 실패:', err);
            });
    }

    // itemList를 Map으로 변환하여 성능 최적화
    const itemMap = useMemo(() => {
        const map = new Map();
        products.forEach(item => {
            map.set(item.id, item.name);
        });
        return map;
    }, [products]);

    return (
        <div className="subscription-table-container">
            <h2>Subscription List</h2>

            {/* 정렬 및 필터링 셀렉트 박스 */}
            <div className="sorting-controls">
                <select
                    onChange={(e) => setSortKey(e.target.value)}
                    defaultValue=""
                    aria-label="정렬 기준 선택"
                >
                    <option value="" disabled>정렬 기준 선택</option>
                    <option value="name">이름</option>
                    <option value="creationDate">날짜</option>
                    <option value="expirationDate">만료 날짜</option>
                    <option value="productNumber">상품 번호</option>
                    <option value="useState">사용 여부</option>
                </select>

                <select
                    onChange={(e) => setSortOrder(e.target.value)}
                    defaultValue="asc"
                    aria-label="정렬 순서 선택"
                >
                    <option value="asc">오름차순</option>
                    <option value="desc">내림차순</option>
                </select>

                <select
                    onChange={(e) => setFilterStatus(e.target.value)}
                    defaultValue="all"
                    aria-label="사용 여부 필터"
                >
                    <option value="all">모두</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            <table className="subscription-table">
                <thead>
                <tr>
                    <th>구매자 이름</th>
                    <th>Hash Key</th>
                    <th>날짜</th>
                    <th>만료 일자</th>
                    <th>MAC Address 1</th>
                    <th>MAC Address 2</th>
                    <th>상품 번호</th>
                    <th>상품명</th>
                    <th>사용 여부</th>
                </tr>
                </thead>
                <tbody>
                {filteredAndSortedSubscriptions.map((sub, index) => (
                    <tr key={index}>
                        <td>{sub.buyerName}</td>
                        <td onClick={handleCopyHashKey}>{sub.hashKey}</td>
                        <td>{new Date(sub.creationDate).toLocaleDateString()}</td>
                        <td>{new Date(sub.expirationDate).toLocaleDateString()}</td>
                        <td>{sub.macAddress1 || 'N/A'}
                            {sub.macAddress1 && (
                                <button
                                    className="delete-button"
                                    onClick={() => handleDeleteClick(sub.productNumber, sub.hashKey, sub.macAddress1)}
                                >
                                    삭제
                                </button>
                            )}
                        </td>
                        <td>{sub.macAddress2 || 'N/A'}
                            {sub.macAddress2 && (
                                <button
                                    className="delete-button"
                                    onClick={() => handleDeleteClick(sub.productNumber, sub.hashKey, sub.macAddress2)}
                                >
                                    삭제
                                </button>
                            )}
                        </td>
                        <td>{sub.productNumber}</td>
                        <td>{itemMap.get(sub.productNumber) || 'N/A'}</td>
                        <td>{sub.useState}</td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Alert Modal */}
            {alertVisible && (
                <div className="alert-modal-backdrop">
                    <div className="alert-modal">
                        <p>{selectedMacAddress}를 삭제하시겠습니까?</p>
                        <div className="alert-modal-buttons">
                            <button className="confirm-button" onClick={handleConfirmDelete}>
                                Yes
                            </button>
                            <button className="cancel-button" onClick={handleCancelDelete}>
                                No
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionTable;
